/**
 * Several requests, one after another, stopping at the first failure. The
 * console's multi-request actions — Add games ×N, delete a division's pools
 * then the division, rewrite every game's time after a timezone change — go
 * through this so a page can say exactly how far an action got. Each step is
 * handed the previous step's result, which is how a new game's time and place
 * follow from the game created before it.
 */
export interface SequenceResult<T> {
  /** How many steps resolved. Equal to `steps.length` when nothing failed. */
  done: number;
  results: T[];
  /** What the first failing step threw; absent when every step landed. */
  error?: unknown;
}

export async function runSequentially<T>(
  steps: Array<(previous: T | undefined) => Promise<T>>,
): Promise<SequenceResult<T>> {
  const results: T[] = [];
  for (const step of steps) {
    try {
      results.push(await step(results[results.length - 1]));
    } catch (error) {
      return { done: results.length, results, error };
    }
  }
  return { done: results.length, results };
}
